import {
    AzureLayer,
    getLayerFilter,
    getMinAndMaxZoomOpts, isBubbleLayer,
    isHeatMapDisabledXorIsOverlayPinEnabled
} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {layer, Map, source, SymbolLayerOptions} from "azure-maps-control";
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {SymbolThematicStyler} from "@esWebAPI/es/map/styler/SymbolThematicStyler";
import {ColorThematic, SizeThematic, SymbolThematic} from "@esWebAPI/es/model/Thematic";
import {DataDrivenPropertyValueSpecification, ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {SizeThematicStyler} from "../../styler/SizeThematicStyler";
import {AzureImageSpriteManager} from "../AzureImageSpriteManager";
import {SymbolType} from "@esWebAPI/es/model/SymbolType";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";

export class AzureSymbolLayer implements AzureLayer {

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager
    private readonly spriteManager: AzureImageSpriteManager;
    private readonly spriteGenerator: ImageSpriteGenerator;
    private readonly colorStyler: ColorThematicStyler;
    private readonly sizeStyler: SizeThematicStyler;
    private readonly symbolStyler: SymbolThematicStyler

    private _layerView: ILayerView;
    private _layerInfo: LayerInfo;
    private _layer: layer.SymbolLayer|undefined;

    private colorThematic: ColorThematic|undefined;
    private sizeThematic: SizeThematic|undefined;
    private symbolThematic: SymbolThematic|undefined;
    private symbolTypes: SymbolType[] = [];
    private layerFilter: ExpressionFilterSpecification|undefined;

    private datasourceId: string|undefined;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                colorStyler: ColorThematicStyler,
                sizeStyler: SizeThematicStyler,
                symbolStyler: SymbolThematicStyler,
                layerFilter?: ExpressionFilterSpecification,
                spriteGenerator?: ImageSpriteGenerator) {
        this._map = map;
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        this.datasourceManager = datasourceManager;
        this.spriteManager = spriteManager;
        this.colorStyler = colorStyler
        this.sizeStyler = sizeStyler;
        this.symbolStyler = symbolStyler;

        this.layerFilter = layerFilter;

        this.spriteGenerator = spriteGenerator ?? new ImageSpriteGenerator(spriteManager);
    }

    private async updateThematics(): Promise<void> {
        this.colorThematic = await this.colorStyler.getThematic(this._layerView);
        this.sizeThematic = await this.sizeStyler.getThematic(this._layerView);
        this.symbolThematic = await this.symbolStyler.getThematic(this._layerView);
    }

    private async generateSymbolImages() {

        const symbolTypeIds: number[] = [];

        const defaultStyle = this._layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            this._layerView.styleSettings.defaultStyle.symbolStyle :
            this._layerView.styleSettings.defaultStyle;

        symbolTypeIds.push(defaultStyle.symbolTypeId);
        if (this.symbolThematic) {
            this.symbolThematic.ranges.forEach((r: any) => {
                if (!symbolTypeIds.includes(r.symbolTypeId)) {
                    symbolTypeIds.push(r.symbolTypeId);
                }
            });
        }
        this.symbolTypes = await es.api.symbolType.getByIds(symbolTypeIds);

        await this.spriteGenerator.generateImages(this._layerView, this.symbolThematic, this.sizeThematic, this.colorThematic);
    }

    private getOptions(): SymbolLayerOptions {


        const symbolExp = this.symbolStyler.getExpression(this._layerView, this._layerInfo, this.symbolThematic);
        const sizeExp = this.sizeStyler.getIconSizeExpression(this._layerView, this.sizeThematic);
        const colorExp = this.colorStyler.getServerExpression(this._layerView, this.colorThematic);
        const image = [
            'case',
            ['in', ['concat', 'es_', symbolExp, '_', sizeExp], ['literal', this.spriteManager.imageIds()]],
            ['concat', 'es_', symbolExp, '_', sizeExp],
            ['concat', 'es_', symbolExp, '_', sizeExp, '_', colorExp]
        ] as DataDrivenPropertyValueSpecification<string>;

        const minAndMaxZoomOpts = getMinAndMaxZoomOpts(this._layerView);

        return {
            sourceLayer: this.datasourceId && this.datasourceManager.isPbf(this.datasourceId) ? 'es' : undefined,
            iconOptions: {
                anchor: this.symbolStyler.getAnchor(this._layerView, this._layerInfo, this.symbolTypes, this.symbolThematic),
                allowOverlap: true,
                ignorePlacement: true,
                image,
                size: this.sizeStyler.getSizeScaleExpression(this._layerView, this.sizeThematic),
                opacity: this.colorStyler.getOpacity(this._layerView)
            },
            minZoom: minAndMaxZoomOpts.minZoom,
            maxZoom: minAndMaxZoomOpts.maxZoom,
            filter: getLayerFilter(this._layerView, this.layerFilter),
            visible: this._layerView.enabled && isHeatMapDisabledXorIsOverlayPinEnabled(this._layerView)  && !isBubbleLayer(this._layerView, this._layerInfo)
        };
    }

    public async create(datasource: source.Source,
                 colorThematic?: ColorThematic,
                 sizeThematic?: SizeThematic,
                 symbolThematic?: SymbolThematic): Promise<void> {



        this.datasourceId = datasource.getId();
        this.colorThematic = colorThematic;
        this.sizeThematic = sizeThematic;
        this.symbolThematic = symbolThematic;

        this._layer = new layer.SymbolLayer(datasource.getId(), this.getLayerId(), {
            sourceLayer: this.datasourceId && this.datasourceManager.isPbf(this.datasourceId) ? 'es' : undefined,
            visible: false
        });
        this._map.layers.add(this._layer);

        if (!isBubbleLayer(this._layerView, this._layerInfo)) {
            await this.generateSymbolImages();
        }
        this._layer.setOptions(this.getOptions());
    }

    public async recreate(layerView: ILayerView, layerInfo: LayerInfo,
                   datasource: source.Source,
                   colorThematic?: ColorThematic,
                   sizeThematic?: SizeThematic,
                   symbolThematic?: SymbolThematic) {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        return this.create(datasource, colorThematic, sizeThematic, symbolThematic);
    }

    public async update(layerView: ILayerView, layerInfo: LayerInfo): Promise<void> {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        await this.updateThematics();
        await this.generateSymbolImages();
        let options = this.getOptions();
        this._layer?.setOptions(options);
    }

    public setLayerFilter(filter?: ExpressionFilterSpecification) {
        this.layerFilter = filter;
    }

    public hide(): void {
        this._layer?.setOptions({visible: false});
    }

    public show(): void {
        this._layer?.setOptions({visible: true});
    }

    public remove(): void {
        const layerId = this.getLayerId();
        if (this._map.layers.getLayerById(layerId)) {
            this._map.layers.remove(layerId);
        }
        this._layer = undefined;
    }

    public hideFeature(featureId: number) {
        const opacity = ['case',
            ['==', ['id'], featureId],
            0,
            this.colorStyler.getOpacity(this._layerView)
        ] as DataDrivenPropertyValueSpecification<number>;
        this._layer?.setOptions({iconOptions: {opacity: opacity}});
    }

    public showFeatures() {
        const opacity = this.colorStyler.getOpacity(this._layerView);
        this._layer?.setOptions({iconOptions: {opacity: opacity}});
    }

    public getLayerId(): string {
        return "symbol_" + this._layerView.id + "_" + this.datasourceId;
    }
}
